import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const readJSON = (text, fallback) => { try { return JSON.parse(text); } catch { return fallback; } };
const config = readJSON(document.getElementById('vadrouk-data')?.textContent || '{}', {});
const text = (key) => config.strings?.[key] || key;
const catalog = config.products || [];
const money = (value) => new Intl.NumberFormat(config.locale || 'fr-FR', { style: 'currency', currency: config.currency || 'EUR' }).format(value / 100);
const storage = {
  get(key, fallback) { try { return readJSON(localStorage.getItem(key), fallback) ?? fallback; } catch { return fallback; } },
  set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } },
  remove(key) { try { localStorage.removeItem(key); } catch {} },
};
storage.remove('vadrouk_duo_v1');
const el = (tag, className, content) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (content != null) node.textContent = content;
  return node;
};
const activeVariants = (product) => product.variants.filter((variant) => variant.available);
let cartProductIds = new Set(config.cartIds || []);
let adding = false;

async function addItems(items, owner) {
  if (!items.length || adding) return;
  adding = true;
  owner.setAttribute('aria-busy', 'true');
  const status = owner.querySelector('[data-vad-status]');
  if (status) status.textContent = text('adding');
  const pending = CartLinesUpdateEvent.createPromise();
  pending.promise.catch(() => {});
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))];
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: pending.promise }));
  try {
    const response = await fetch(`${config.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }) });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || text('cart_error'));
    const cartResponse = await fetch(`${config.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(text('cart_refresh_error'));
    const cart = await cartResponse.json();
    cartProductIds = new Set(cart.items.map((item) => item.product_id));
    pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: owner.id || 'vadrouk-selection', itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections: result.sections, didError: false } });
    if (status) status.textContent = text('added');
    if (config.tracking && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'add_to_cart', ecommerce: { currency: config.currency, items: items.map((item) => ({ item_id: String(item.id), quantity: item.quantity })) } });
    }
  } catch (error) {
    pending.reject(error);
    if (status) status.textContent = error.message || text('cart_error');
  } finally {
    adding = false;
    owner.removeAttribute('aria-busy');
  }
}

function productCard(product, formId) {
  const article = el('article', 'vad-card');
  const link = el('a', 'vad-card__image');
  link.href = product.url;
  if (product.image) {
    const image = el('img'); image.src = product.image; image.alt = product.title;
    image.loading = 'lazy'; image.width = 480; image.height = 480; link.append(image);
  } else link.append(el('span', 'vad-placeholder', text('image_pending')));
  article.append(link);
  const heading = el('h3', 'vad-card__title'); const titleLink = el('a', '', product.title); titleLink.href = product.url; heading.append(titleLink); article.append(heading);
  const variants = activeVariants(product);
  const price = el('p', 'vad-card__price', money(product.price)); article.append(price);
  const label = el('label', 'vad-small', text('variant'));
  const select = el('select', 'vad-variant'); select.id = `${formId}-${product.id}`; label.htmlFor = select.id;
  select.dataset.productId = String(product.id); select.required = false;
  if (variants.length > 1) { const empty = el('option', '', text('choose_variant')); empty.value = ''; select.append(empty); }
  for (const variant of variants) { const option = el('option', '', variant.title === 'Default Title' ? text('single_variant') : variant.title); option.value = String(variant.id); option.dataset.price = String(variant.price); select.append(option); }
  const imageNote = el('p', 'vad-image-note vad-small'); imageNote.hidden = true;
  const updateVariant = () => {
    const variant = variants.find((item) => String(item.id) === select.value);
    price.textContent = money(variant?.price ?? product.price);
    const image = link.querySelector('img');
    const source = variant?.image || product.image;
    if (image && source) { image.src = source; image.alt = variant?.image ? `${product.title} — ${variant.title}` : product.title; }
    const target = new URL(product.url, location.origin);
    if (variant) target.searchParams.set('variant', variant.id);
    link.href = target.href; titleLink.href = target.href;
    imageNote.hidden = !variant || Boolean(variant.image) || variants.length < 2;
    imageNote.textContent = imageNote.hidden ? '' : text('variant_photo_pending');
  };
  select.addEventListener('change', updateVariant);
  updateVariant();
  article.append(imageNote);
  article.append(label, select);
  return article;
}

function renderSelection(host, products, { quiz = false } = {}) {
  host.replaceChildren();
  const owner = host.closest('vadrouk-selection, vadrouk-quiz, vadrouk-recent');
  if (!products.length || !owner) return;
  const form = el('form', 'vad-selection-form');
  const grid = el('div', 'vad-grid');
  const groupId = `vad-${crypto.randomUUID()}`;
  for (const product of products) {
    const card = productCard(product, groupId);
    const label = el('label', 'vad-check vad-select-toggle'); const check = el('input'); check.type = 'checkbox'; check.name = 'selected'; check.value = String(product.id);
    const toggleText = el('span', 'vad-toggle-text', text('include_product')); label.append(check, toggleText); card.append(label); grid.append(card);
    // A deliberate variant choice also includes this item in the selection.
    card.querySelector('select').addEventListener('change', (event) => {
      check.checked = Boolean(event.currentTarget.value);
    });
  }
  const total = el('p', 'vad-selection-total', text('nothing_selected')); total.setAttribute('role', 'status');
  const summary = el('div', 'vad-selection-summary');
  const button = el('button', 'vad-button', quiz ? text('pack_cart') : text('add_selection')); button.type = 'submit'; button.disabled = true;
  const review = () => {
    let count = 0; let amount = 0; let valid = true;
    for (const card of grid.children) {
      const checked = card.querySelector('input').checked;
      card.classList.toggle('is-selected', checked);
      card.querySelector('.vad-toggle-text').textContent = checked ? text('selected_product') : text('include_product');
      card.querySelector('select').required = checked;
      if (!card.querySelector('input').checked) continue;
      count++;
      const selected = card.querySelector('select').selectedOptions[0];
      if (!selected?.value) valid = false;
      else amount += Number(selected.dataset.price);
    }
    button.disabled = !count || !valid;
    total.textContent = count ? `${text('selection_total')} ${money(amount)}${valid ? '' : ` — ${text('choose_variants')}`}` : text('nothing_selected');
  };
  form.addEventListener('change', review);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const items = [...grid.children].filter((card) => card.querySelector('input').checked).map((card) => ({ id: Number(card.querySelector('select').value), quantity: 1 }));
    if (items.some((item) => !Number.isSafeInteger(item.id) || !item.id)) return;
    button.disabled = true;
    await addItems(items, owner);
    review();
  });
  summary.append(total, button); form.append(grid, summary); host.append(form);
}

class VadroukDialogs extends Component {
  connectedCallback() {
    super.connectedCallback(); this.abort?.abort(); this.abort = new AbortController();
    const signal = this.abort.signal;
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-vad-open-quiz], [data-vad-close], [data-vad-print]');
      if (!target) return;
      if (target.hasAttribute('data-vad-print')) return window.print();
      if (target.hasAttribute('data-vad-close')) return target.closest('dialog')?.close();
      const dialog = document.getElementById('vadrouk-quiz-dialog');
      if (!dialog || dialog.open) return;
      event.preventDefault(); this.opener = target;
      dialog.showModal();
    }, { signal });
    for (const dialog of document.querySelectorAll('#vadrouk-quiz-dialog')) {
      dialog.addEventListener('close', () => this.opener?.focus(), { signal });
      dialog.addEventListener('click', (event) => { if (event.target === dialog) { const bounds = dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close(); } }, { signal });
    }
    for (const note of document.querySelectorAll('[data-vad-size-advice]')) note.textContent = text('size_advice');
  }
  disconnectedCallback() { this.abort?.abort(); super.disconnectedCallback(); }
}

class VadroukQuiz extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.started) return; this.started = true;
    this.step = 0; this.answers = {}; this.body = this.querySelector('[data-vad-quiz-body]');
    const query = new URLSearchParams(location.search);
    const saved = Object.fromEntries(['activity', 'size', 'weather', 'duration'].map((key) => [key, query.get(key)]));
    this.questions = [
      ['activity', 'q_activity', [['road-trip', 'car'], ['moto-dog', 'motorcycle'], ['hors-piste', 'hiking'], ['bivouac', 'camping']]],
      ['size', 'q_size', [['S', 'size_s'], ['M', 'size_m'], ['L', 'size_l'], ['XL', 'size_xl']]],
      ['weather', 'q_weather', [['warm', 'warm'], ['cold', 'cold'], ['rain', 'rain'], ['all', 'all_weather']]],
      ['duration', 'q_duration', [['walk', 'walk'], ['weekend', 'weekend'], ['days', 'days']]],
    ];
    if (this.questions.every(([key, , choices]) => choices.some(([value]) => value === saved[key]))) { this.answers = saved; this.step = 4; }
    this.render();
  }
  render() {
    this.body.hidden = false; this.body.replaceChildren();
    if (this.step >= 4) return this.results();
    const [key, title, choices] = this.questions[this.step];
    const progress = el('progress', 'vad-progress'); progress.max = 4; progress.value = this.step;
    progress.setAttribute('aria-label', `${text('step')} ${this.step + 1} / 4`);
    const legend = el('legend', 'vad-quiz-question', text(title));
    const fieldset = el('fieldset', 'vad-quiz-options'); fieldset.append(legend);
    const current = this.answers[key] || null;
    for (const [value, label] of choices) {
      const button = el('button', 'vad-choice', text(label)); button.type = 'button'; button.setAttribute('aria-pressed', String(value === current));
      button.addEventListener('click', () => { this.answers[key] = value; this.step++; this.render(); this.body.querySelector('legend, h3')?.focus(); });
      fieldset.append(button);
    }
    legend.tabIndex = -1;
    this.body.append(progress, el('p', 'vad-small', `${text('step')} ${this.step + 1} / 4`), fieldset);
    if (this.step) { const back = el('button', 'vad-link', text('back')); back.type = 'button'; back.addEventListener('click', () => { this.step--; this.render(); }); this.body.append(back); }
  }
  results() {
    const heading = el('h3', '', text('your_selection')); heading.tabIndex = -1;
    this.body.append(heading, el('p', 'vad-small', text('selection_advice')));
    const candidates = catalog.filter((product) => product.available && !product.handle.startsWith('pack-') && product.activities.includes(this.answers.activity));
    const score = (product) => {
      let value = 0;
      if (product.sizes?.includes(this.answers.size)) value += 8;
      if (this.answers.activity === 'road-trip' && /housse|coffre|banquette|siège|panier/.test(product.title.toLocaleLowerCase('fr'))) value += 14;
      const name = product.title.toLocaleLowerCase('fr');
      const weather = this.answers.weather;
      if (weather === 'cold' && /polaire|softshell/.test(name)) value += 6;
      if (weather === 'warm' && /gourde/.test(name)) value += 6;
      if (weather === 'rain' && /imperméable|softshell/.test(name)) value += 6;
      if (this.answers.duration !== 'walk' && /gamelle|gourde|lit de camp/.test(name)) value += 2;
      return value;
    };
    const ranked = [...candidates].sort((a, b) => score(b) - score(a));
    const primaryPatterns = { 'road-trip': /housse|coffre|banquette/, 'moto-dog': /sac-de-transport/, 'hors-piste': /harnais-/, 'bivouac': /lit-de-camp/ };
    const primary = ranked.find(product => primaryPatterns[this.answers.activity]?.test(product.handle));
    const choices = {
      'road-trip': ['gourde-de-voyage-pour-chien-avec-bol', 'gamelle-de-voyage-pliable-pastel'],
      'moto-dog': ['gourde-de-voyage-pour-chien-avec-bol', 'gamelle-de-voyage-pliable-pastel'],
      'hors-piste': ['laisse-mains-libres-running-et-randonnee', this.answers.weather === 'rain' ? 'impermeable-chien-cape-jaune-capuche' : 'gourde-de-voyage-pour-chien-avec-bol'],
      'bivouac': ['tapis-rafraichissant-pour-chien', 'gamelle-de-voyage-pliable-pastel'],
    };
    const extras = (choices[this.answers.activity] || []).map(handle => catalog.find(product => product.available && product.handle === handle)).filter(Boolean);
    const chosen = [primary, ...extras].filter(Boolean);
    const results = el('div'); this.body.append(results); renderSelection(results, chosen, { quiz: true });
    if (!chosen.length) { this.body.append(el('p', '', text('selection_empty'))); const link = el('a', 'vad-button', text('collections')); link.href = `${config.root}collections/${this.answers.activity}`; this.body.append(link); }
    const handle = this.answers.activity === 'road-trip' ? 'pack-road-trip' : this.answers.activity === 'bivouac' ? 'pack-bivouac' : this.answers.activity === 'hors-piste' ? 'pack-vadrouille' : null;
    const pack = catalog.find((product) => product.handle === handle && product.available);
    if (pack) { const link = el('a', 'vad-pack-link', `${text('matching_pack')} ${pack.title} · ${money(pack.price)}`); link.href = pack.url; this.body.append(link); }
    const share = el('a', 'vad-link', text('share_selection')); const url = new URL(`${config.root}pages/preparer-ma-sortie`, location.origin); for (const [key, value] of Object.entries(this.answers)) url.searchParams.set(key, value); share.href = url.href;
    const restart = el('button', 'vad-link', text('restart')); restart.type = 'button'; restart.addEventListener('click', () => { this.step = 0; this.answers = {}; this.render(); });
    this.body.append(share, restart);
  }
}

class VadroukSelection extends Component {
  connectedCallback() {
    super.connectedCallback(); this.abort?.abort(); this.abort = new AbortController(); this.render();
    document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
      try { const result = await event.promise; if (result?.cart?.lines) cartProductIds = new Set(result.detail?.items?.map((item) => item.product_id) || [...cartProductIds]); if (this.isConnected && this.dataset.context === 'drawer') this.render(); } catch {}
    }, { signal: this.abort.signal });
  }
  render() {
    const box = this.querySelector('[data-vad-suggestions]');
    const current = catalog.find((product) => String(product.id) === this.dataset.productId);
    const inCart = catalog.filter((product) => cartProductIds.has(product.id));
    const activity = current?.activities.find((value) => ['road-trip', 'moto-dog', 'hors-piste', 'bivouac'].includes(value)) || inCart.flatMap((product) => product.activities).find((value) => ['road-trip', 'moto-dog', 'hors-piste', 'bivouac'].includes(value)) || 'road-trip';
    const pairs = (this.dataset.pairs || '').split(',');
    const candidates = catalog.filter((product) => product.available && !product.handle.startsWith('pack-') && product !== current && !cartProductIds.has(product.id));
    const priority = (product) => {
      let score = pairs.includes(String(product.id)) ? 30 : product.activities.includes(activity) ? 5 : 0;
      if (activity === 'road-trip') {
        if (/gourde/.test(product.handle)) score += 20;
        if (/gamelle/.test(product.handle)) score += 16;
        if (/tapis-rafraichissant/.test(product.handle)) score += 10;
      }
      return score;
    };
    const products = candidates.sort((a, b) => priority(b) - priority(a)).slice(0, this.dataset.context === 'drawer' ? 2 : 3);
    box.replaceChildren(); if (!products.length) return;
    const inner = el('div');
    if (this.dataset.context === 'drawer') {
      const details = el('details', 'vad-drawer-offers');
      details.append(el('summary', '', text('complete_outing')), inner); box.append(details);
    } else { box.append(el('h3', '', text('complete_outing')), inner); }
    renderSelection(inner, products);
  }
  disconnectedCallback() { this.abort?.abort(); super.disconnectedCallback(); }
}

class VadroukTabs extends Component {
  connectedCallback() {
    super.connectedCallback(); this.abort?.abort(); this.abort = new AbortController();
    this.addEventListener('click', (event) => { const button = event.target.closest('[data-vad-tab]'); if (button) this.choose(button.dataset.vadTab); }, { signal: this.abort.signal });
    this.addEventListener('keydown', (event) => {
      const tabs = [...this.querySelectorAll('[data-vad-tab]')]; const index = tabs.indexOf(event.target);
      if (index < 0 || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.choose(tabs[next].dataset.vadTab); tabs[next].focus();
    }, { signal: this.abort.signal });
    this.choose(this.querySelector('[data-vad-tab]')?.dataset.vadTab);
  }
  choose(activity) {
    if (!activity || !this.querySelector(`[data-vad-tab="${activity}"]`)) return;
    for (const tab of this.querySelectorAll('[data-vad-tab]')) { const selected = tab.dataset.vadTab === activity; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; }
    for (const panel of this.querySelectorAll('[data-vad-panel]')) panel.hidden = panel.dataset.vadPanel !== activity;
  }
  disconnectedCallback() { this.abort?.abort(); super.disconnectedCallback(); }
}

for (const [name, type] of [['vadrouk-dialogs', VadroukDialogs], ['vadrouk-quiz', VadroukQuiz], ['vadrouk-selection', VadroukSelection], ['vadrouk-tabs', VadroukTabs]]) if (!customElements.get(name)) customElements.define(name, type);

const pageProduct = document.querySelector('[data-vad-current-product]')?.dataset.vadCurrentProduct;
if (pageProduct) {
  const recent = storage.get('vadrouk_recent_v1', []);
  const handles = Array.isArray(recent) ? recent.filter((value) => typeof value === 'string') : [];
  storage.set('vadrouk_recent_v1', [pageProduct, ...handles.filter((value) => value !== pageProduct)].slice(0, 8));
  const box = document.querySelector('[data-vad-recent]');
  const products = handles.filter((value) => value !== pageProduct).map((handle) => catalog.find((product) => product.handle === handle)).filter(Boolean).slice(0, 3);
  if (box && products.length) { box.hidden = false; const grid = box.querySelector('.vad-grid'); for (const product of products) { const card = productCard(product, 'vad-recent'); card.querySelector('select').remove(); card.querySelector('label').remove(); grid.append(card); } }
}

if (config.tracking && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) {
  window.dataLayer = window.dataLayer || [];
  const product = catalog.find((item) => item.handle === pageProduct);
  if (product) window.dataLayer.push({ event: 'view_item', ecommerce: { currency: config.currency, value: product.price / 100, items: [{ item_id: String(product.id), item_name: product.title, price: product.price / 100 }] } });
  document.addEventListener('click', (event) => { if (event.target.closest('[name="checkout"], a[href$="/checkout"]')) window.dataLayer.push({ event: 'begin_checkout' }); });
}

// VADROUK — présentation des avis authentiques déjà publiés, 17 octobre 2026.
(() => {
  const reviewWidget = document.getElementById('judgeme_product_reviews');
  if (!reviewWidget) return;
  const style = document.createElement('style');
  style.textContent = `
  .vad-product-rating{display:inline-flex;align-items:center;gap:10px;flex-wrap:wrap;color:#2f4a3a;font:600 .9rem/1.5 var(--font-body--family,inherit);text-decoration:none;margin:10px 0 16px;min-height:32px}
  .vad-product-rating,.vad-product-rating>span:last-child{font-family:Arial,sans-serif!important;font-size:14px!important;font-weight:600!important;line-height:1.4!important}#judgeme_product_reviews{scroll-margin-top:110px}.vad-rating-stars{position:relative;display:inline-block;letter-spacing:2px;font:20px/1 Arial,sans-serif;color:#c4cfc3}
  .vad-rating-stars>span{position:absolute;inset:0 auto 0 0;overflow:hidden;white-space:nowrap;color:#2f4a3a}
  #judgeme_product_reviews .jm-review-widget{--jm-widget-primary-color:#2f4a3a!important;--jm-review-widget-star-and-histogram-color:#2f4a3a!important;--jm-review-widget-button-color:#2f4a3a!important;--jm-review-widget-button-text-color:#fffcf7!important;--jm-review-widget-text-color:#1f2326!important;--jm-review-widget-form-button-color:#2f4a3a!important;background:#f6f1e9;border-radius:18px;padding:24px}
  #judgeme_product_reviews .jm-review-widget-minimal-header__title{font-family:var(--font-heading--family,Georgia,serif);color:#2f4a3a}
  #judgeme_product_reviews .vad-reviews-carousel{display:flex!important;gap:18px!important;overflow-x:auto!important;height:auto!important;position:relative!important;padding:8px 2px 20px;scroll-snap-type:x mandatory;scroll-padding-inline:2px;scrollbar-width:thin;scrollbar-color:#2f4a3a #e9eee5;overscroll-behavior-inline:contain}
  #judgeme_product_reviews .vad-reviews-carousel>.jdgm-masonry-grid__masonry-item{position:relative!important;transform:none!important;flex:0 0 min(360px,80vw)!important;width:min(360px,80vw)!important;opacity:1!important;scroll-snap-align:start;min-width:0}
  #judgeme_product_reviews .jdgm-review-card{background:#fffcf7!important;border:1px solid #2f4a3a24!important;border-radius:14px!important;height:100%}
  .vad-reviews-nav{display:flex;align-items:center;justify-content:flex-end;gap:8px;margin:0 0 12px;color:#2f4a3a}
  .vad-reviews-nav button{display:grid;place-items:center;width:44px;height:44px;border:1px solid #2f4a3a40;border-radius:50%;background:#2f4a3a;color:#fffcf7;cursor:pointer;font-size:22px}
  .vad-reviews-nav button:disabled{background:#e9eee5;color:#778577;cursor:default}.vad-reviews-nav button:focus-visible,.vad-product-rating:focus-visible{outline:3px solid #e07a3f;outline-offset:3px}
  @media(max-width:749px){#judgeme_product_reviews .jm-review-widget{padding:20px 12px}.vad-product-rating{font-size:.84rem}}
  `;
  document.head.append(style);
  const enhance = () => {
    const widget = document.getElementById('judgeme_product_reviews');
    if (!widget) return;
    const average = widget.querySelector('.jm-average-rating-display');
    const values = average ? [...average.querySelectorAll('p')].map(p => p.textContent.trim()) : [];
    const rating = Number.parseFloat((values[0] || '').replace(',', '.'));
    const count = Number.parseInt((values[1] || '').replace(/[^0-9]/g, ''), 10);
    const title = document.querySelector('main h1');
    if (title && rating >= 0 && rating <= 5 && count > 0) {
      let badge = document.querySelector('[data-vad-product-rating]');
      if (!badge) { badge = document.createElement('a'); badge.dataset.vadProductRating = ''; badge.className = 'vad-product-rating'; title.insertAdjacentElement('afterend', badge); }
      const signature = `${widget.dataset.productId}:${rating}:${count}`;
      if (badge.dataset.signature !== signature) {
        badge.dataset.signature = signature;
        badge.href = '#judgeme_product_reviews';
        badge.setAttribute('aria-label', `${rating.toLocaleString('fr-FR')} sur 5, ${count} avis. Lire les avis.`);
        const stars = document.createElement('span'); stars.className = 'vad-rating-stars'; stars.setAttribute('aria-hidden', 'true'); stars.textContent = '★★★★★';
        const fill = document.createElement('span'); fill.textContent = '★★★★★'; fill.style.width = `${rating / 5 * 100}%`; stars.append(fill);
        const text = document.createElement('span'); text.textContent = `${rating.toLocaleString('fr-FR', {minimumFractionDigits:1, maximumFractionDigits:1})}/5 · ${count} avis`;
        badge.replaceChildren(stars, text);
      }
    }
    const grid = widget.querySelector('.jdgm-masonry-grid__container');
    if (!grid) return;
    grid.classList.add('vad-reviews-carousel');
    const items = grid.querySelectorAll('[role="listitem"]');
    if (items.length > 1 && !widget.querySelector('[data-vad-reviews-nav]')) {
      const nav = document.createElement('div'); nav.className = 'vad-reviews-nav'; nav.dataset.vadReviewsNav = '';
      const previous = document.createElement('button'); previous.type = 'button'; previous.textContent = '←'; previous.setAttribute('aria-label', 'Avis précédents');
      const next = document.createElement('button'); next.type = 'button'; next.textContent = '→'; next.setAttribute('aria-label', 'Avis suivants');
      nav.append(previous, next); grid.before(nav);
      const move = direction => grid.scrollBy({left: direction * ((grid.querySelector('[role="listitem"]')?.getBoundingClientRect().width || 300) + 18), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      previous.addEventListener('click', () => move(-1)); next.addEventListener('click', () => move(1));
      const update = () => { previous.disabled = grid.scrollLeft < 3; next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 3; };
      grid.addEventListener('scroll', update, {passive:true}); new ResizeObserver(update).observe(grid); requestAnimationFrame(update);
    }
  };
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; enhance(); }); };
  new MutationObserver(schedule).observe(reviewWidget, {childList:true, subtree:true});
  document.addEventListener('shopify:section:load', schedule);
  enhance();
})();

(() => {
  const productMain = document.querySelector('main');
  if (!productMain?.querySelector('variant-picker')) return;
  const style = document.createElement('style');
  style.textContent = `
    .vad-size-legend{width:100%;display:flex!important;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
    .vad-size-trigger{display:inline-flex;align-items:center;gap:7px;min-height:48px;padding:6px 0 6px 12px;border:0;background:transparent;color:#2f4a3a;font:600 13px/1.3 Arial,sans-serif;text-decoration:underline;text-underline-offset:4px;cursor:pointer}
    .vad-size-trigger:focus-visible,.vad-fit-close:focus-visible{outline:2px solid #2f4a3a;outline-offset:3px}
    .vad-fit-dialog{width:min(600px,calc(100vw - 24px));max-height:85dvh;margin:auto;border:1px solid #d5ddd2;border-radius:20px;padding:0;background:#fffcf7;color:#253b2f;overflow:auto;overscroll-behavior:contain;box-shadow:0 20px 80px #17251e33}
    .vad-fit-dialog::backdrop{background:#17251e88}
    .vad-fit-header{position:sticky;top:0;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 20px;background:#fffcf7;border-bottom:1px solid #d5ddd2}
    .vad-fit-header h2{margin:0;font-size:22px;color:#2f4a3a}
    .vad-fit-close{display:grid;place-items:center;min-width:48px;min-height:48px;border:1px solid #d5ddd2;border-radius:50%;background:transparent;color:#2f4a3a;font:26px/1 Arial,sans-serif;cursor:pointer}
    .vad-fit-dialog .vad-size-guide{border:0;margin:0;border-radius:0;background:transparent}
    .vad-fit-dialog .vad-size-guide>summary{display:none}
    .vad-fit-dialog .vad-size-guide__body{border:0}
    @media(max-width:749px){.vad-fit-dialog{max-height:88dvh;border-radius:18px}.vad-fit-header{padding:10px 16px}.vad-fit-header h2{font-size:20px}}
  `;
  document.head.append(style);
  let serial = 0;
  const enhance = () => {
    const picker = document.querySelector('main variant-picker');
    if (!picker) return;
    const legend = [...picker.querySelectorAll('fieldset legend')].find(el => /^(taille|size|dimensions?)\s*$/i.test(el.childNodes[0]?.textContent?.trim() || el.textContent.trim()));
    if (!legend) return;
    const visibleCard = document.querySelector('main .vad-fit-card');
    if (visibleCard) {
      legend.classList.add('vad-size-legend');
      let link = legend.querySelector('.vad-size-trigger');
      if (!link) {
        link = document.createElement('button');
        link.type = 'button';
        link.className = 'vad-size-trigger';
        link.textContent = text('choose_size_button');
        legend.append(link);
      }
      link.removeAttribute('aria-haspopup');
      link.setAttribute('aria-controls', visibleCard.id);
      return;
    }
    const guides = [...document.querySelectorAll('main .vad-size-guide:not(.vad-care-guide)')].filter(el => !el.closest('dialog'));
    let dialog = document.querySelector('.vad-fit-dialog');
    const guide = guides.find(el => el.closest('.vad-product-help')) || guides[0];
    if (guide) {
      if (dialog?.open) dialog.close();
      dialog?.remove();
      dialog = document.createElement('dialog');
      dialog.className = 'vad-fit-dialog';
      dialog.id = `vad-fit-dialog-${++serial}`;
      const title = guide.querySelector('summary strong')?.textContent?.trim() || 'Guide des tailles';
      const header = document.createElement('div');
      header.className = 'vad-fit-header';
      const heading = document.createElement('h2');
      heading.id = `${dialog.id}-title`;
      heading.textContent = title;
      dialog.setAttribute('aria-labelledby', heading.id);
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'vad-fit-close';
      close.textContent = '×';
      close.setAttribute('aria-label', document.documentElement.lang.startsWith('fr') ? 'Fermer le guide des tailles' : 'Close size guide');
      close.addEventListener('click', () => dialog.close());
      header.append(heading, close);
      guide.open = true;
      dialog.append(header, guide);
      document.body.append(dialog);
      dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
    }
    if (!dialog) return;
    legend.classList.add('vad-size-legend');
    let button = legend.querySelector('.vad-size-trigger');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'vad-size-trigger';
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', () => document.querySelector('.vad-fit-dialog')?.showModal());
      legend.append(button);
    }
    button.setAttribute('aria-controls', dialog.id);
    const label = text('choose_size_button');
    if (button.textContent !== label) button.textContent = label;
  };
  let queued = false;
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; enhance(); }); } };
  new MutationObserver(schedule).observe(productMain, {childList:true,subtree:true});
  document.addEventListener('shopify:section:load', schedule);
  enhance();
})();

(() => { const style = document.createElement("style"); style.textContent = `.vad-size-trigger{min-height:48px;padding:11px 16px;border:1px solid #2f4a3a;border-radius:999px;background:#2f4a3a;color:#fffcf7;font-size:14px;text-decoration:none;box-shadow:0 3px 10px #2f4a3a16}.vad-size-trigger::before{content:"";font-size:21px;line-height:1}.vad-size-trigger:hover{background:#1c3627}.vad-size-trigger:focus-visible{outline:3px solid #e07a3f;outline-offset:3px}.vad-size-trigger:active{transform:translateY(1px)}`; document.head.append(style); })();

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-vad-buy-now]');
  if (!button) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const form = button.closest('form');
  if (!form || button.disabled || !form.reportValidity()) return;
  const data = new FormData(form);
  const variant = form.querySelector('[name="id"]');
  if (!variant?.value || variant.disabled) return;
  const error = button.parentElement.querySelector('.vad-buy-now-error');
  if (error) { error.hidden = true; error.textContent = ''; }
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(button.dataset.cartAddUrl, {method:'POST',body:data,headers:{Accept:'application/json'}});
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || button.dataset.errorMessage);
    window.location.assign(button.dataset.checkoutUrl);
  } catch (failure) {
    if (error) { error.textContent = failure.message || button.dataset.errorMessage; error.hidden = false; }
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}, true);

document.addEventListener('click', event => {
  const button = event.target.closest('[data-vad-product-prev],[data-vad-product-next]');
  if (!button) return;
  const scope = button.closest('[role="tabpanel"]') || button.closest('.vad-section');
  const rail = scope?.querySelector('.vad-product-rail');
  if (!rail) return;
  const item = rail.firstElementChild;
  const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).gap || 0) : rail.clientWidth;
  const direction = button.hasAttribute('data-vad-product-prev') ? -1 : 1;
  rail.scrollBy({left:direction * step,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
});

// Shopify Forms is isolated in an open shadow root. Keep its accessible text
// and button typography aligned with the storefront, without changing the form.
(() => {
  if (!document.querySelector('shopify-forms-embed')) return;
  let attempts = 0;
  const alignGuideTypography = () => {
    const roots = [...document.querySelectorAll('shopify-forms-embed')]
      .map(embed => embed.shadowRoot).filter(root => root?.querySelector('button'));
    roots.forEach(root => {
      if (root.querySelector('[data-vadrouk-form-type]')) return;
      const style = document.createElement('style');
      style.setAttribute('data-vadrouk-form-type', '');
      style.textContent = 'h2{font:400 28px/1.1 "Caveat Brush",cursive!important;font-synthesis:none}p{font-family:Kalam,"Segoe Print",sans-serif!important;font-size:16px!important;line-height:1.55!important}button{font-family:Kalam,"Segoe Print",sans-serif!important;font-weight:700!important}input{font-size:16px!important}label,a{font-size:14px!important;line-height:1.5!important}';
      root.append(style);
    });
    return roots.length > 0;
  };
  if (alignGuideTypography()) return;
  const timer = setInterval(() => {
    attempts += 1;
    if (alignGuideTypography() || attempts >= 40) clearInterval(timer);
  }, 500);
})();

(() => {
  const packs = {
    'pack-road-trip': [
      { handle: 'grille-de-separation-voiture-pour-chien-pawhut' },
      { handle: 'tapis-rafraichissant-pour-chien', fixed: ['Gris — vagues', '100x150 cm'] },
      { handle: 'gourde-de-voyage-pour-chien-avec-bol', option: 0 },
    ],
    'pack-bivouac': [
      { handle: 'lit-de-camp-sureleve-pour-chien-pawhut' },
      { handle: 'tapis-rafraichissant-pour-chien', option: 0, size: '50x70 cm' },
      { handle: 'gamelle-de-voyage-pliable-pastel', option: 1 },
    ],
    'pack-vadrouille': [
      { handle: 'sac-a-dos-de-randonnee-pour-chien', option: 0 },
      { handle: 'laisse-mains-libres-running-et-randonnee', option: 1 },
      { handle: 'gamelle-de-voyage-pliable-pastel', option: 2 },
    ],
  };
  const initialPicker = document.querySelector('main variant-picker[data-product-handle]');
  if (!packs[initialPicker?.dataset.productHandle]) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  let mode = 'composition';
  let scheduled = false;
  const render = () => {
    const picker = document.querySelector('main variant-picker[data-product-handle]');
    const specification = packs[picker?.dataset.productHandle];
    const section = picker?.closest('.shopify-section');
    const gallery = section?.querySelector('media-gallery');
    if (!specification || !gallery) return;
    const choices = [...picker.querySelectorAll('fieldset')].map(field => field.querySelector('input:checked')?.value);
    const selectedPack = catalog.find(item => item.handle === picker.dataset.productHandle);
    const selectedPackVariant = selectedPack?.variants.find(variant => variant.options.every((value, index) => value === choices[index]));
    const confirmedVariant = readJSON(picker.querySelector('script[type="application/json"]')?.textContent || '{}', {});
    if (selectedPackVariant && String(confirmedVariant.id) === String(selectedPackVariant.id)) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== String(selectedPackVariant.id)) {
        url.searchParams.set('variant', selectedPackVariant.id);
        history.replaceState(history.state, '', url);
      }
    }
    const parts = specification.map(part => {
      const product = catalog.find(item => item.handle === part.handle);
      const values = part.fixed || (part.option == null ? [] : [choices[part.option], ...(part.size ? [part.size] : [])]);
      const variant = product?.variants.find(item => values.every((value, index) => value === item.options[index]));
      const color = values[0];
      const packshot = product?.packshots?.find(photo => !color || normalize(photo.alt).includes(normalize(color)));
      const image = packshot?.image || variant?.image || product?.image;
      return { product, variant, image, color, values };
    });
    if (parts.some(part => !part.product || !part.variant || !part.image)) return;
    let viewer = section.querySelector('.vad-bundle-viewer');
    if (!viewer) {
      viewer = el('div', 'vad-bundle-viewer');
      const controls = el('div', 'vad-bundle-viewer__controls');
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', text('bundle_view'));
      for (const [value, label] of [['composition', 'bundle_composition'], ['lifestyle', 'bundle_lifestyle']]) {
        const button = el('button', '', text(label));
        button.type = 'button';
        button.dataset.bundleMode = value;
        button.addEventListener('click', () => { mode = value; render(); });
        controls.append(button);
      }
      const grid = el('div', 'vad-bundle-composition');
      grid.id = `vad-composition-${picker.dataset.productId}`;
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', text('bundle_composition'));
      viewer.append(controls, grid, el('p', 'vad-bundle-viewer__note', text('bundle_preview_note')));
      gallery.before(viewer);
    }
    const signature = JSON.stringify(parts.map(part => [part.variant.id, part.image]));
    let summary = section.querySelector('.vad-bundle-summary');
    if (!summary) {
      summary = el('div', 'vad-bundle-summary');
      summary.setAttribute('role', 'status');
      summary.setAttribute('aria-live', 'polite');
      picker.after(summary);
    }
    if (viewer.dataset.signature !== signature || summary.dataset.signature !== signature) {
      viewer.dataset.signature = signature;
      summary.dataset.signature = signature;
      const grid = viewer.querySelector('.vad-bundle-composition');
      grid.replaceChildren();
      summary.replaceChildren();
      const summaryTitle = el('p', 'vad-bundle-summary__title', text('bundle_selected'));
      const summaryImages = el('div', 'vad-bundle-summary__items');
      summary.append(summaryTitle, summaryImages);
      for (const part of parts) {
        const caption = part.values.length ? part.values.join(' · ') : text('single_variant');
        const figure = el('figure', 'vad-bundle-composition__item');
        figure.dataset.componentHandle = part.product.handle;
        const image = el('img');
        image.width = 720;
        image.height = 720;
        image.src = part.image;
        image.alt = `${part.product.title} — ${caption}`;
        image.decoding = 'async';
        const label = el('figcaption');
        label.append(el('strong', '', part.product.title), el('span', '', caption));
        figure.append(image, label);
        grid.append(figure);
        const miniature = el('figure');
        const thumbnail = el('img');
        thumbnail.src = part.image.replace(/([?&])width=\d+/, '$1width=160');
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        thumbnail.alt = `${part.product.title} — ${caption}`;
        thumbnail.width = 80;
        thumbnail.height = 80;
        miniature.append(thumbnail, el('figcaption', '', caption));
        summaryImages.append(miniature);
      }
    }
    viewer.querySelectorAll('[data-bundle-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bundleMode === mode)));
    viewer.querySelector('.vad-bundle-composition').hidden = mode !== 'composition';
    viewer.querySelector('.vad-bundle-viewer__note').hidden = mode !== 'composition';
    gallery.classList.toggle('vad-bundle-native-hidden', mode === 'composition');
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; render(); });
  };
  document.addEventListener('change', event => {
    if (event.target.closest('main variant-picker')) schedule();
  });
  const main = document.querySelector('main');
  if (main) new MutationObserver(schedule).observe(main, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', schedule);
  render();
})();


